if (!customElements.get('bundle-xsell')) {
  customElements.define(
    'bundle-xsell',
    class extends HTMLElement {
      connectedCallback() {
        this.discountPct = parseFloat(this.dataset.discountPct) || 0;
        this.discountMechanism = this.dataset.discountMechanism || 'shopify';
        this.bundleName = this.dataset.bundleName || 'Bundle';
        this.mainVariantId = parseInt(this.dataset.mainVariantId, 10);
        this.mainPrice = parseInt(this.dataset.mainPrice, 10) || 0;
        this.mainTitle = this.dataset.mainTitle || '';
        this.currency = this.dataset.currency || 'USD';

        this.checkboxes = Array.from(this.querySelectorAll('.bundle-xsell__checkbox'));

        // Commandeer the existing buy_buttons submit button. If the product is a
        // gift card we leave the original form fully alone (it carries the recipient
        // fields and the bundle metafield path isn't a fit).
        if (this.dataset.isGiftCard !== 'true') {
          this.attachToOriginalButton();
        }

        this.checkboxes.forEach((cb) => cb.addEventListener('change', () => this.updateState()));
        this.updateState();
      }

      attachToOriginalButton() {
        const section = this.closest('section') || this.closest('[id^="MainProduct"]') || document;
        const form = section.querySelector('product-form');
        if (!form) return;
        const submit = form.querySelector('button[name="add"]');
        if (!submit) return;

        this.originalForm = form;
        this.originalSubmit = submit;
        this.originalLabelSpan = submit.querySelector('span');
        this.originalLabelHTML = this.originalLabelSpan ? this.originalLabelSpan.innerHTML : '';

        // Capture-phase click handler so we run before product-form's submit logic.
        submit.addEventListener(
          'click',
          (e) => {
            const selected = this.getSelected();
            if (selected.length === 0) return; // let the native form submit
            e.preventDefault();
            e.stopImmediatePropagation();
            this.submitBundle(selected);
          },
          true
        );
      }

      getSelected() {
        return this.checkboxes
          .filter((cb) => cb.checked && !cb.disabled)
          .map((cb) => ({
            id: parseInt(cb.dataset.variantId, 10),
            price: parseInt(cb.dataset.price, 10) || 0,
            title: cb.dataset.title,
          }));
      }

      formatMoney(cents) {
        const noFraction = cents % 100 === 0;
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
            style: 'currency',
            currency: this.currency,
            minimumFractionDigits: noFraction ? 0 : 2,
            maximumFractionDigits: 2,
          }).format(cents / 100);
        } catch (_) {
          return '$' + (noFraction ? Math.round(cents / 100) : (cents / 100).toFixed(2));
        }
      }

      updateState() {
        if (!this.originalLabelSpan) return;
        const selected = this.getSelected();
        const hasAny = selected.length > 0;

        if (hasAny) {
          const extrasTotal = selected.reduce((s, i) => s + i.price, 0);
          const bundleTotal = this.mainPrice + extrasTotal;
          const savings = Math.round(bundleTotal * (this.discountPct / 100));
          const finalTotal = bundleTotal - savings;
          this.originalLabelSpan.innerHTML = `<span class="inline-flex items-center">Add bundle &mdash; <s class="bundle-xsell__btn-original">${this.formatMoney(bundleTotal)}</s>&nbsp;${this.formatMoney(finalTotal)}</span>`;
          this.originalSubmit.classList.add('bundle-xsell--bundle-mode');
        } else {
          this.originalLabelSpan.innerHTML = this.originalLabelHTML;
          this.originalSubmit.classList.remove('bundle-xsell--bundle-mode');
        }
      }

      async submitBundle(selected) {
        const submit = this.originalSubmit;
        if (!submit) return;

        const cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        submit.setAttribute('aria-disabled', 'true');
        submit.classList.add('loading');
        const spinner = submit.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        if (cart && typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(document.activeElement);
        }

        const componentInputs = selected.map((s) => ({
          id: s.id,
          quantity: 1,
          title: s.title,
          price: s.price,
        }));

        const sections = this.getCartSections(cart);

        try {
          const result =
            this.discountMechanism === 'flex_bundles'
              ? await this.addFlexBundle({
                  components: componentInputs,
                  discountPercentage: this.discountPct,
                  bundleName: this.bundleName,
                  sections,
                })
              : await this.addBundleAsLineItems({
                  components: componentInputs,
                  bundleName: this.bundleName,
                  sections,
                });

          if (result && result.status && Number(result.status) >= 400) {
            throw new Error(result.description || 'Failed to add bundle');
          }

          // Mirror product-form.js: publish cartUpdate so subscribers (cart icon,
          // shipping countdown, free-gift logic, etc.) react properly.
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'bundle-xsell',
              productVariantId: this.mainVariantId,
              cartData: result,
            });
          }

          if (cart) {
            if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
            if (typeof cart.renderContents === 'function') {
              cart.renderContents(result);
            }
          }

          this.checkboxes.forEach((cb) => (cb.checked = false));
          this.updateState();
        } catch (err) {
          console.error('[bundle-xsell] add failed', err);
        } finally {
          submit.removeAttribute('aria-disabled');
          submit.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        }
      }

      getCartSections(cart) {
        if (cart && typeof cart.getSectionsToRender === 'function') {
          return cart.getSectionsToRender().map((s) => s.id);
        }
        return ['cart-drawer', 'cart-icon-bubble'];
      }

      // Primary path: Flex Bundles "Component Sum" mode. Bundle total = sum of
      // component prices. Each price is the *discounted* per-item value, computed
      // from the block's discount_pct setting (dynamic, not hardcoded). The parent
      // variant is included as the first component so it contributes to the total
      // at its discounted price too.
      // Requires the PDP product to be configured as a Flex Bundle in the app.
      // Ref: https://flexbundles.com/docs (Cart API Integration)
      async addFlexBundle({ components, discountPercentage, bundleName, sections }) {
        const factor = (100 - discountPercentage) / 100;
        const discountedPrice = (cents) => Number((((cents || 0) * factor) / 100).toFixed(2));

        const componentPayload = [
          { id: this.mainVariantId, quantity: 1, price: discountedPrice(this.mainPrice) },
          ...components.map((c) => ({
            id: c.id,
            quantity: c.quantity,
            price: discountedPrice(c.price),
          })),
        ];

        const properties = {
          _components: JSON.stringify(componentPayload),
          _settings: JSON.stringify({ title: bundleName }),
        };
        properties['Item 1'] = this.mainTitle;
        components.forEach((c, idx) => {
          properties['Item ' + (idx + 2)] = c.title;
        });

        const body = {
          items: [{ id: this.mainVariantId, quantity: 1, properties }],
          sections: sections || ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        };

        const res = await fetch(this.cartRoute('cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        return res.json();
      }

      // Fallback path: add the PDP product + each cross-sell as separate line items
      // tagged with a shared _bundle_id. Pricing is handled by a Shopify automatic
      // discount (Buy-X-Get-Y) configured in admin: when this PDP product + any
      // xsell co-exist in cart, apply the matching % off. No client-side price math.
      async addBundleAsLineItems({ components, bundleName, sections }) {
        const bundleId =
          'bxs_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
        const sharedProps = { _bundle_id: bundleId, Bundle: bundleName };

        const items = [
          { id: this.mainVariantId, quantity: 1, properties: { ...sharedProps } },
          ...components.map((c) => ({
            id: c.id,
            quantity: c.quantity,
            properties: { ...sharedProps },
          })),
        ];

        const body = {
          items,
          sections: sections || ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        };

        const res = await fetch(this.cartRoute('cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        return res.json();
      }

      cartRoute(path) {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        return root + path;
      }

    }
  );
}
